import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Divider, Empty, Select, Alert, Spin, Table, Tag,
} from 'antd';
import {
  LineChartOutlined, ApiOutlined, TeamOutlined, BarChartOutlined, KeyOutlined, PieChartOutlined,
} from '@ant-design/icons';
import { Line, Column } from '@ant-design/plots';
import { ColumnsType } from 'antd/es/table';
import { ApiService } from '../services/api';

const { Text, Title } = Typography;

const CARD_STYLE: React.CSSProperties = { width: '100%' };
const SUB_STYLE: React.CSSProperties = {
  fontSize: 12,
  display: 'block',
  marginTop: 4,
  minHeight: 18,
};

// 「全部客戶」的哨兵值。用不可能成為 org_name 的字串，避免與真實客戶撞名。
const ALL_ORGS = '__ALL_ORGS__';
const MA_SHORT = 7;
const MA_LONG = 30;

const S_DAILY = '每日請求數';
const S_MA7 = `${MA_SHORT} 日移動平均`;
const S_MA30 = `${MA_LONG} 日移動平均`;

interface DailyRow {
  request_date: string;
  org_name: string;
  total_requests: number;
  successful_requests: number;
  error_requests: number;
  rolling_7day_avg: number;
  rolling_30day_avg: number;
}

interface ApiDailyAnalysisProps {
  data: {
    data?: Array<Record<string, any>>;
    time_range?: { start_date?: string; end_date?: string };
  } | null;
}

// 只在視窗完整時輸出移動平均。視窗未滿就照算的話，開頭幾天會是不完整視窗的
// 平均值，畫出來像流量從低點爬升，與事實不符。
const movingAverage = (
  series: Array<{ date: string; value: number }>,
  window: number
): Array<{ date: string; value: number }> =>
  series
    .map((_, i) => {
      if (i < window - 1) return null;
      const slice = series.slice(i - window + 1, i + 1);
      const avg = slice.reduce((s, p) => s + p.value, 0) / window;
      return { date: series[i].date, value: Math.round(avg * 100) / 100 };
    })
    .filter(Boolean) as Array<{ date: string; value: number }>;

const ApiDailyAnalysis: React.FC<ApiDailyAnalysisProps> = ({ data }) => {
  const rows: DailyRow[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as unknown as DailyRow[]) : []),
    [data]
  );

  const startDate = data?.time_range?.start_date;
  const endDate = data?.time_range?.end_date;

  const [selectedOrg, setSelectedOrg] = useState<string>(ALL_ORGS);

  const [endpointRows, setEndpointRows] = useState<any[]>([]);
  const [channelRows, setChannelRows] = useState<any[]>([]);
  const [keywordRows, setKeywordRows] = useState<any[]>([]);
  const [extraLoading, setExtraLoading] = useState(false);

  useEffect(() => {
    if (!startDate) return;
    let cancelled = false;
    setExtraLoading(true);
    const params = { start_date: startDate, end_date: endDate };
    Promise.all([
      ApiService.getApiQsearchEndpoints(params).catch(() => null),
      ApiService.getApiQsearchChannels(params).catch(() => null),
      ApiService.getApiQsearchKeywords(params).catch(() => null),
    ])
      .then(([ep, ch, kw]) => {
        if (cancelled) return;
        setEndpointRows(Array.isArray(ep?.data) ? ep.data : []);
        setChannelRows(Array.isArray(ch?.data) ? ch.data : []);
        setKeywordRows(Array.isArray(kw?.data) ? kw.data : []);
      })
      .finally(() => {
        if (!cancelled) setExtraLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const orgTotals = useMemo(() => {
    const acc: Record<string, number> = {};
    rows.forEach((r) => {
      acc[r.org_name] = (acc[r.org_name] || 0) + r.total_requests;
    });
    return Object.entries(acc)
      .map(([org_name, total]) => ({ org_name, total }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  const isAll = selectedOrg === ALL_ORGS;

  // 目前選取對象的每日序列
  const dailySeries = useMemo(() => {
    const perDay: Record<string, number> = {};
    rows
      .filter((r) => isAll || r.org_name === selectedOrg)
      .forEach((r) => {
        perDay[r.request_date] = (perDay[r.request_date] || 0) + r.total_requests;
      });
    return Object.keys(perDay)
      .sort()
      .map((date) => ({ date, value: perDay[date] }));
  }, [rows, selectedOrg, isAll]);

  // 單一客戶時直接用後端算好的移動平均（含 30 天暖身，較準）；
  // 「全部客戶」是跨客戶加總，後端沒有這個數列，只能在前端算。
  const chartData = useMemo(() => {
    const out: Array<{ date: string; series: string; value: number }> = dailySeries.map((p) => ({
      date: p.date,
      series: S_DAILY,
      value: p.value,
    }));

    if (isAll) {
      movingAverage(dailySeries, MA_SHORT).forEach((p) =>
        out.push({ date: p.date, series: S_MA7, value: p.value })
      );
      movingAverage(dailySeries, MA_LONG).forEach((p) =>
        out.push({ date: p.date, series: S_MA30, value: p.value })
      );
    } else {
      rows
        .filter((r) => r.org_name === selectedOrg)
        .sort((a, b) => a.request_date.localeCompare(b.request_date))
        .forEach((r) => {
          out.push({ date: r.request_date, series: S_MA7, value: Number(r.rolling_7day_avg ?? 0) });
          out.push({ date: r.request_date, series: S_MA30, value: Number(r.rolling_30day_avg ?? 0) });
        });
    }
    return out;
  }, [dailySeries, rows, selectedOrg, isAll]);

  const totals = useMemo(() => {
    const scoped = rows.filter((r) => isAll || r.org_name === selectedOrg);
    const total = scoped.reduce((s, r) => s + r.total_requests, 0);
    const days = new Set(scoped.map((r) => r.request_date));
    return {
      total,
      errors: scoped.reduce((s, r) => s + r.error_requests, 0),
      orgs: orgTotals.length,
      days: days.size,
      avgPerDay: days.size ? Math.round(total / days.size) : 0,
      peak: dailySeries.reduce(
        (best, p) => (p.value > best.value ? p : best),
        { date: '—', value: 0 }
      ),
    };
  }, [rows, selectedOrg, isAll, orgTotals, dailySeries]);

  // 下方三個區塊同樣跟著下拉選單連動
  const endpointTotals = useMemo(() => {
    const acc: Record<string, number> = {};
    endpointRows
      .filter((r: any) => isAll || r.org_name === selectedOrg)
      .forEach((r: any) => {
        const key = r.endpoint || '未分類';
        acc[key] = (acc[key] || 0) + (r.total_requests || 0);
      });
    return Object.entries(acc)
      .map(([endpoint, total_requests]) => ({ endpoint, total_requests }))
      .sort((a, b) => b.total_requests - a.total_requests)
      .slice(0, 15);
  }, [endpointRows, selectedOrg, isAll]);

  const channelTotals = useMemo(() => {
    const acc: Record<string, number> = {};
    channelRows
      .filter((r: any) => isAll || r.org_name === selectedOrg)
      .forEach((r: any) => {
        const key = r.channel || '未指定';
        acc[key] = (acc[key] || 0) + (r.total_requests || 0);
      });
    return Object.entries(acc)
      .map(([channel, total_requests]) => ({ channel, total_requests }))
      .sort((a, b) => b.total_requests - a.total_requests);
  }, [channelRows, selectedOrg, isAll]);

  // keywords 端點同時回傳全站(global_keyword)與逐客戶(organization_keyword)兩種列，
  // 依選取對象取用對應的那一種。
  const topKeywords = useMemo(() => {
    const wanted = isAll ? 'global_keyword' : 'organization_keyword';
    return keywordRows
      .filter((r: any) => r.row_type === wanted && (isAll || r.org_name === selectedOrg))
      .sort((a: any, b: any) => (b.usage_count || 0) - (a.usage_count || 0))
      .slice(0, 20);
  }, [keywordRows, selectedOrg, isAll]);

  if (!rows.length) {
    return <Empty description="此區間沒有 API 呼叫紀錄" style={{ padding: '60px 0' }} />;
  }

  const scopeLabel = isAll ? '全部客戶' : selectedOrg;

  // @ant-design/plots v2 (G2 5) 設定：分色用 colorField，座標軸用 axis，
  // 線條樣式用 style；v1 的 seriesField / xAxis / lineStyle 在此版本不生效。
  const lineConfig: any = {
    data: chartData,
    xField: 'date',
    yField: 'value',
    colorField: 'series',
    scale: {
      color: { range: ['#1677ff', '#fa8c16', '#52c41a'] },
    },
    style: {
      lineWidth: (items: any) => {
        const s = Array.isArray(items) ? items[0]?.series : items?.series;
        return s === S_DAILY ? 2 : 2.5;
      },
      lineDash: (items: any) => {
        const s = Array.isArray(items) ? items[0]?.series : items?.series;
        return s === S_DAILY ? undefined : [5, 4];
      },
    },
    axis: {
      y: { labelFormatter: (v: number) => Number(v).toLocaleString() },
      x: { labelAutoRotate: true },
    },
    legend: { color: { position: 'bottom', layout: { justifyContent: 'center' } } },
    tooltip: { items: [{ channel: 'y', valueFormatter: (v: number) => Number(v).toLocaleString() }] },
  };

  const columnConfig: any = {
    data: endpointTotals,
    xField: 'endpoint',
    yField: 'total_requests',
    colorField: 'endpoint',
    legend: false,
    axis: {
      x: { labelAutoRotate: true, labelAutoHide: false },
      y: { labelFormatter: (v: number) => Number(v).toLocaleString() },
    },
  };

  const keywordColumns: ColumnsType<any> = [
    {
      title: '關鍵字',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (v: string) => <Text code copyable={{ text: v }}>{v}</Text>,
    },
    {
      title: '使用次數',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 110,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.usage_count || 0) - (b.usage_count || 0),
      render: (v: number) => (v || 0).toLocaleString(),
    },
    ...(isAll
      ? [
          {
            title: '使用客戶數',
            dataIndex: 'org_count',
            key: 'org_count',
            width: 110,
            sorter: (a: any, b: any) => (a.org_count || 0) - (b.org_count || 0),
          } as any,
        ]
      : []),
    {
      title: '最後使用',
      dataIndex: 'last_usage_date',
      key: 'last_usage_date',
      width: 130,
      render: (v: string) => (v ? String(v).slice(0, 10) : '—'),
    },
  ];

  const orgSelect = (
    <Select
      showSearch
      style={{ minWidth: 300 }}
      value={selectedOrg}
      onChange={setSelectedOrg}
      optionFilterProp="label"
      options={[
        { label: `全部客戶（${orgTotals.length} 家加總）`, value: ALL_ORGS },
        ...orgTotals.map((o) => ({
          label: `${o.org_name}（${o.total.toLocaleString()}）`,
          value: o.org_name,
        })),
      ]}
    />
  );

  return (
    <>
      {data?.time_range && (
        <Text type="secondary">
          統計區間：{startDate} ~ {endDate || '今天'}（已排除 QSearch 內部流量）
        </Text>
      )}
      <Divider />

      <Row gutter={16} style={{ marginBottom: 24 }} align="stretch">
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title={<span><ApiOutlined /> 期間請求總數</span>} value={totals.total} />
            <Text type="secondary" style={SUB_STYLE}>平均每日 {totals.avgPerDay.toLocaleString()}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title={<span><TeamOutlined /> 目前檢視</span>} value={isAll ? totals.orgs : 1} suffix="家" />
            <Text type="secondary" style={SUB_STYLE}>{scopeLabel}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title={<span><LineChartOutlined /> 單日最高</span>} value={totals.peak.value} />
            <Text type="secondary" style={SUB_STYLE}>{totals.peak.date}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><BarChartOutlined /> 錯誤請求</span>}
              value={totals.errors}
              valueStyle={totals.errors > 0 ? { color: '#cf1322' } : undefined}
            />
            <Text type="secondary" style={SUB_STYLE}>
              佔 {totals.total ? ((totals.errors / totals.total) * 100).toFixed(1) : 0}%
            </Text>
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span><LineChartOutlined /> 每日呼叫趨勢 — {scopeLabel}</span>
            {orgSelect}
          </div>
        }
        style={{ marginBottom: 24 }}
      >
        <Line {...lineConfig} height={420} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          實線為每日請求數，虛線為移動平均。
          {isAll
            ? `全部客戶的移動平均在前端加總後計算，視窗未滿的前 ${MA_SHORT - 1} / ${MA_LONG - 1} 天不繪製。`
            : '單一客戶的移動平均由後端計算，已含 30 天暖身資料。'}
        </Text>
      </Card>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="本表僅保有近期資料，圖表天數會少於所選區間"
        description="ApiQsearchQuery 目前只保有 2026-06-22 之後的資料。圖表只畫到實際有資料的第一天為止，不會把更早的日期補成 0——那是「沒有資料」而非「當天沒人呼叫」。"
      />

      <Card
        title={<span><BarChartOutlined /> API 端點使用量 — {scopeLabel}（前 15 名）</span>}
        style={{ marginBottom: 24 }}
      >
        {extraLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }} aria-busy="true">
            <Spin size="large" />
          </div>
        ) : endpointTotals.length ? (
          <Column {...columnConfig} height={360} />
        ) : (
          <Empty description={`${scopeLabel} 在此區間沒有端點資料`} />
        )}
      </Card>

      <Row gutter={16} align="stretch">
        <Col xs={24} lg={14} style={{ display: 'flex', marginBottom: 24 }}>
          <Card
            title={<Title level={5} style={{ margin: 0 }}><KeyOutlined /> 最常搜尋的關鍵字 — {scopeLabel}</Title>}
            style={CARD_STYLE}
          >
            {extraLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }} aria-busy="true">
                <Spin />
              </div>
            ) : (
              <Table
                dataSource={topKeywords}
                columns={keywordColumns}
                rowKey={(r: any) => r.keyword}
                size="small"
                pagination={{ pageSize: 10, showSizeChanger: false }}
                locale={{ emptyText: `${scopeLabel} 在此區間沒有可解析的關鍵字` }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10} style={{ display: 'flex', marginBottom: 24 }}>
          <Card
            title={<Title level={5} style={{ margin: 0 }}><PieChartOutlined /> 渠道使用量 — {scopeLabel}</Title>}
            style={CARD_STYLE}
          >
            {extraLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }} aria-busy="true">
                <Spin />
              </div>
            ) : channelTotals.length ? (
              <div>
                {channelTotals.map((c) => {
                  const top = channelTotals[0].total_requests || 1;
                  const pct = (c.total_requests / top) * 100;
                  return (
                    <div key={c.channel} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <Tag>{c.channel}</Tag>
                        <Text>{c.total_requests.toLocaleString()}</Text>
                      </div>
                      <div style={{ background: '#f0f0f0', height: 8, borderRadius: 4 }}>
                        <div
                          style={{
                            width: `${Math.max(pct, 1)}%`,
                            background: '#1677ff',
                            height: 8,
                            borderRadius: 4,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty description={`${scopeLabel} 在此區間沒有渠道資料`} />
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ApiDailyAnalysis;
